/**
 * @fileOverview This file contains the implementation of the AgendaCalendar component
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region  Imports
import '../../scss/Components/MaintenanceAgenda/_calendar.scss';
import { useState, useEffect } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Typography,
  tableCellClasses,
  Grid,
} from '@mui/material';
import moment from 'moment';
import YearCalendarDialog from './YearCalendarDialog';
import '../../scss/Components/MaintenanceAgenda/_calendar.scss';

//#endregion

const AgendaCalendar = ({ assets, selectedAssets }) => {
  const months = [
    'ENE',
    'FEB',
    'MAR',
    'ABR',
    'MAY',
    'JUN',
    'JUL',
    'AGO',
    'SEP',
    'OCT',
    'NOV',
    'DIC',
  ];

  const [selectedYear, setSelectedYear] = useState(moment());
  const [openYearSelection, setOpenYearSelection] = useState(false);
  const [weeksByMonth, setWeeksByMonth] = useState([]);

  const calculateWeeksByMonth = (year) => {
    const weeks = Array(12)
      .fill()
      .map(() => []);
    const startOfYear = moment(`${year}-01-01`).startOf('week');
    const endOfYear = moment(`${year}-12-31`).endOf('week');

    let currentWeek = startOfYear;

    while (
      currentWeek.isBefore(endOfYear) ||
      currentWeek.isSame(endOfYear, 'week')
    ) {
      const month = currentWeek.month();
      const weekNumber = currentWeek.isoWeek();
      if (!weeks[month].some((week) => week.weekNumber === weekNumber)) {
        weeks[month].push({ weekNumber, weekStart: currentWeek.clone() });
      }
      currentWeek.add(1, 'week');
    }

    // Eliminar duplicados en el último mes (diciembre)
    weeks[11] = weeks[11]
      .filter(
        (week, index, self) =>
          index === self.findIndex((w) => w.weekNumber === week.weekNumber)
      )
      .sort((a, b) => a.weekNumber - b.weekNumber);

    setWeeksByMonth(weeks);
  };

  useEffect(() => {
    calculateWeeksByMonth(selectedYear.year());
  }, [selectedYear]);

  const getAdjustedWeekIndex = (date) => {
    const startOfYear = moment(`${date.year()}-01-01`).startOf('week');
    const weekIndex = date.diff(startOfYear, 'weeks');

    // Ajuste para que los últimos días de un mes se muestren en la última semana del mes
    const endOfMonth = date.clone().endOf('month');
    if (date.isSame(endOfMonth, 'week')) {
      return weekIndex - 1;
    }

    return weekIndex;
  };

  const recurrenceMapping = {
    1: 'M', // Mensual
    2: 'B', // Bimestral
    3: 'T', // Trimestral
    4: 'C', // Cuatrimestral
    6: 'S', // Semestral
    0: 'U', // N/A
  };

  const filteredAssets = assets.filter(
    (asset) => selectedAssets.length === 0 || selectedAssets.includes(asset)
  );

  return (
    <TableContainer className="agenda-table-container">
      <Table
        className="agenda-table"
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderLeft: 'none',
            borderTop: 'none',
          },
        }}
      >
        <TableHead>
          <TableRow className="agenda-table-months-header">
            <TableCell align="center" size="small" className="agenda-year-cell">
              <Button
                variant="contained"
                onClick={() => setOpenYearSelection(true)}
                className="agenda-year-btn"
              >
                {selectedYear.format('YYYY')}
              </Button>
              <YearCalendarDialog
                open={openYearSelection}
                close={() => setOpenYearSelection(false)}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
              />
            </TableCell>
            {months.map((month, index) => (
              <TableCell
                colSpan={weeksByMonth[index] ? weeksByMonth[index].length : 0}
                key={index}
                align="center"
                size="small"
              >
                {month}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              align="center"
              size="small"
              className="agenda-asset-cell"
            ></TableCell>
            {weeksByMonth.flat().map((week, index) => (
              <TableCell key={index} align="center" size="small">
                <Typography className="agenda-week-text">
                  {week.weekNumber}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAssets.map((asset) => {
            const assetMatrix = Array(52).fill({
              value: '',
              recurrence: null,
            });

            asset.visits
              .filter((visit) => visit.year === selectedYear.year())
              .forEach((visit) => {
                const visitDate = moment(visit.date);
                const weekIdx = getAdjustedWeekIndex(visitDate);
                const recurrenceLabel =
                  recurrenceMapping[visit.recurrence] || '';
                assetMatrix[weekIdx] = {
                  value: recurrenceLabel,
                  recurrence: visit.recurrence,
                };
              });

            return (
              <TableRow key={asset.id}>
                <TableCell align="center" className="agenda-asset-cell">
                  <Grid
                    className="agenda-asset-box"
                    sx={{
                      backgroundColor:
                        asset.priority === 1
                          ? '#FFCECE'
                          : asset.priority === 2
                          ? '#FFEEB0'
                          : '#C8FFBF',
                    }}
                  >
                    <Typography className="agenda-asset-text">
                      {asset.name}
                    </Typography>
                  </Grid>
                </TableCell>
                {assetMatrix.map((week, weekIdx) => (
                  <TableCell
                    className="agenda-visits-value"
                    key={weekIdx}
                    align="center"
                    sx={{
                      color:
                        week.recurrence === 1
                          ? '#5288DA'
                          : week.recurrence === 3
                          ? '#FF3A8D'
                          : '#F88318',
                      backgroundColor:
                        weeksByMonth.flat()[weekIdx].weekStart.month() % 2 === 0
                          ? '#E7E7E7'
                          : '#DBDBDB',
                    }}
                  >
                    {week.value}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AgendaCalendar;
